import {
  Alert,
  Anchor,
  Avatar,
  Box,
  Card,
  Container,
  Grid,
  Group,
  List,
  LoadingOverlay,
  Title
} from '@mantine/core'
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { useFetch } from '@/lib/hooks'
import { getUrlString } from '@/lib/utils'
import { Site as SiteType } from '@/types'

interface Props {
  initialData?: {
    site: SiteType
  }
}

export default function ArticlesView({ initialData }: Props) {
  // Hooks
  const router = useRouter()

  // Constants
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)

  // Fetch
  const { data: siteData, error: siteError } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site
  })
  const { data: articlesData, error: articlesError } = useFetch([
    siteSlug ? `/${siteSlug}/kb/articles/` : null
  ])

  const isSiteLoading = !siteData && !siteError
  const isArticlesLoading = !articlesData && !articlesError
  const isLoading = isSiteLoading || isArticlesLoading

  if (isLoading) {
    return <LoadingOverlay visible />
  }

  const articlesBySection: Record<string, any> = {}
  for (const article of articlesData || []) {
    if (!(article.section.slug in articlesBySection)) {
      articlesBySection[article.section.slug] = {
        section: article.section,
        articles: []
      }
    }
    articlesBySection[article.section.slug].articles.push(article)
  }

  const groups = Object.values(articlesBySection)
  groups.sort((a, b) => {
    if (a.section.ordering < b.section.ordering) {
      return -1
    } else if (a.section.ordering > b.section.ordering) {
      return 1
    } else {
      return 0
    }
  })

  return (
    <Container px="xs" py="lg" size="xl">
      <Title mb="lg">Tópicos de Ajuda</Title>
      <Alert
        icon={<IconAlertTriangle />}
        title="Erro na listagem de tópicos"
        color="orange"
        hidden={!!groups.length}>
        Não encontramos nenhum tópico de ajuda para listar. Entre em contato conosco.
      </Alert>
      <Grid hidden={!groups.length}>
        {groups.map((group: any) => (
          <Grid.Col key={group.section?.uid} md={6}>
            <Card withBorder sx={{ height: '100%' }}>
              <Box
                component={Link}
                href={`/help/articles/${group.section?.slug}`}
                sx={{ textDecoration: 'none' }}>
                <Group align="center" mb="sm">
                  <Avatar src={group.section?.logo} color="blue">
                    <IconInfoCircle />
                  </Avatar>
                  <Title order={2}>{group.section?.name}</Title>
                </Group>
              </Box>
              <List size="md" spacing="xs" type="ordered">
                {group.articles?.map((article: any) => (
                  <List.Item key={article.uid}>
                    <Anchor
                      component={Link}
                      href={`/help/articles/${group.section?.slug}/${article.slug}`}>
                      {article.title}
                    </Anchor>
                  </List.Item>
                ))}
              </List>
            </Card>
          </Grid.Col>
        ))}
      </Grid>
    </Container>
  )
}
